import './index.css'

import React from 'react'

import { PLAYLIST } from '@disco/constants/ItemTypes'
import type { WatermarkStatuses } from '@disco/constants/WatermarkStatusesTypes'
import { useTranslation } from '@disco/i18n/use-translation'

import type { ExplorerPaneType } from 'types/explorer'

import Lozenge from 'components/ui/lozenge'
import PricingPopover from 'components/ui/pricing-popover'

import playlistOptions from './playlist-options'
import trackOptions from './track-options'
import type { Translate } from './types'

const getLozengeText = (
  text: string,
  shortText: string | undefined,
  short: boolean,
): string => (short ? shortText ?? text : text)

const getPropsBasedOnStatus = (
  type: ExplorerPaneType,
  status: WatermarkStatuses,
  t: Translate,
  listOfAllowedStatuses?: WatermarkStatuses[],
) => {
  const defaultOptions = {
    color: undefined,
    icon: undefined,
    notAllowedStatus: false,
    shortText: undefined,
    strikethrough: undefined,
    text: '',
    tooltip: '',
  }

  if (listOfAllowedStatuses && !listOfAllowedStatuses.includes(status)) {
    return { ...defaultOptions, notAllowedStatus: true }
  }

  const options = type === PLAYLIST ? playlistOptions : trackOptions
  const optionsStatus = options[status]

  if (optionsStatus) {
    return { ...defaultOptions, ...optionsStatus(t) }
  }
}

type Props = {
  listOfAllowedStatuses?: WatermarkStatuses[]
  short?: boolean
  type: ExplorerPaneType
  watermarking_status: WatermarkStatuses
}

const WatermarkingStatusFlag = ({
  listOfAllowedStatuses,
  short = false,
  type,
  watermarking_status,
}: Props): null | React.ReactElement<'div'> => {
  const { t } = useTranslation('common')

  if (!watermarking_status) return null
  const propsBasedOnStatus = getPropsBasedOnStatus(
    type,
    watermarking_status,
    t,
    listOfAllowedStatuses,
  )
  if (!propsBasedOnStatus) return null
  const {
    color,
    icon,
    notAllowedStatus,
    shortText,
    strikethrough,
    text,
    tooltip,
  } = propsBasedOnStatus
  if (notAllowedStatus) return null
  const content = (
    <div className="WatermarkingStatusFlag">
      <Lozenge
        color={color}
        icon={icon}
        strikethrough={strikethrough}
        text={short && icon ? null : getLozengeText(text, shortText, short)}
      />
    </div>
  )

  if (tooltip) {
    return (
      <div className="WatermarkingStatusFlag_PopoverWrapper">
        <PricingPopover
          description={tooltip}
          messageClassName="WatermarkingStatusFlag_PricingPopover"
        >
          {content}
        </PricingPopover>
      </div>
    )
  }

  return content
}

export default WatermarkingStatusFlag
